/**
 * Which keyboard shortcuts a person is offered (Employee rebuild C General).
 *
 * 🔴 THE SHORTCUTS WERE NOT FILTERED AT ALL. The Q panel dropped Create WO for
 * anyone without maintenance:create (daf13c77), but `C W` still opened
 * /maintenance?action=new and `G M` the maintenance list for every role, and
 * the `?` panel and Settings listed both — so an employee was taught two keys
 * that end on "You don't have access to this page".
 *
 * A navigating shortcut is offered when the route map (config/routeAccess)
 * lets this person OPEN its destination, and — where the shortcut names one —
 * they hold the permission its action needs. The same rule for the key
 * handler, the `?` panel and the Settings list, so none of the three can
 * offer what another withholds. Shortcuts that open overlays are everyone's.
 *
 * Separate from config/shortcuts.ts so that file stays plain data with no
 * router import.
 */
import { canOpen, routeFor } from '@/config/routeAccess';
import { hasPermFor } from '@/utils/permissions';
import { employeeTargetFor } from '@/config/employeeExperience';
import { SHORTCUTS, isSequence, type Shortcut } from '@/config/shortcuts';
import type { UserRole } from '@/types';

type ShortcutUser = { role: UserRole; permissions?: string[] | null } | null | undefined;

export function canUseShortcut(user: ShortcutUser, s: Shortcut): boolean {
  if (s.permission && !hasPermFor(user, s.permission)) return false;
  if (s.action.type !== 'navigate') return true;
  // The employee shell is checked against the screen it will actually open.
  const route = routeFor(employeeTargetFor(user, s.action.to).split(/[?#]/)[0] ?? '');
  return route !== null && canOpen(user, route.access);
}

export function shortcutsFor(user: ShortcutUser): Shortcut[] {
  return SHORTCUTS.filter((s) => canUseShortcut(user, s));
}

export function sequenceShortcutsFor(user: ShortcutUser): Shortcut[] {
  return shortcutsFor(user).filter(isSequence);
}

/** The `?` panel's and the Settings page's groups, for this person; a group
 *  left with nothing is dropped. */
export function shortcutGroupsFor(user: ShortcutUser): { title: string; items: Shortcut[] }[] {
  const mine = shortcutsFor(user);
  return [
    { title: 'Actions', items: mine.filter((s) => s.keys[0] === 'C' && isSequence(s)) },
    { title: 'Global', items: mine.filter((s) => !isSequence(s)) },
    { title: 'Navigation (G + key)', items: mine.filter((s) => s.keys[0] === 'G' && isSequence(s)) },
  ].filter((g) => g.items.length > 0);
}
